import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { toast } from "react-toastify";
import { FaLock, FaEye, FaEyeSlash, FaCheckCircle, FaSpinner } from 'react-icons/fa';
import yogBg from '../images/yog-bg.jpg';
import HttpInterceptor from '../lib/HttpInterceptor';
import LoginHeader from '../Components/LoginHeader';
import Footer from '../Components/Footer';

const ResetPassword = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [identifier, setIdentifier] = useState('');
  const [identifierType, setIdentifierType] = useState(''); // 'email' or 'mobile'
  const [otp, setOtp] = useState('');
  const [isValid, setIsValid] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  useEffect(() => {
    const emailParam = searchParams.get('email');
    const mobileParam = searchParams.get('mobile');
    const otpParam = searchParams.get('otp');

    if ((!emailParam && !mobileParam) || !otpParam) {
      toast.error('Invalid reset link. Please request a new OTP.');
      navigate('/forgot-password');
      return;
    }

    const type = emailParam ? 'email' : 'mobile';
    const id = emailParam || mobileParam;

    setIdentifier(id);
    setIdentifierType(type);
    setOtp(otpParam);

    // Verify OTP
    const verifyOTP = async () => {
      try {
        await HttpInterceptor.post('/auth/verify-otp', {
          [type]: id,
          otp: otpParam
        });
        setIsValid(true);
      } catch (err) {
        toast.error('Invalid or expired OTP. Please request a new one.');
        navigate('/forgot-password');
      } finally {
        setIsLoading(false);
      }
    };

    verifyOTP();
  }, [searchParams, navigate]);

  const validationSchema = Yup.object({
    password: Yup.string()
      .min(6, 'Password must be at least 6 characters')
      .max(12, 'Password must be at most 12 characters')
      .matches(/[a-zA-Z]/, 'Password must contain at least one letter')
      .matches(/[0-9]/, 'Password must contain at least one number')
      .required('Password is required'),
    confirmPassword: Yup.string()
      .oneOf([Yup.ref('password')], 'Passwords must match')
      .required('Please confirm your password')
  });

  const handleSubmit = async (values, { setSubmitting, setErrors }) => {
    try {
      await HttpInterceptor.post('/auth/reset-password', {
        [identifierType]: identifier,
        otp,
        password: values.password,
        confirmPassword: values.confirmPassword
      });
      toast.success('Password reset successfully!');
      setIsSuccess(true);
      setTimeout(() => {
        navigate('/login');
      }, 2000);
    } catch (err) {
      const msg = err.response?.data?.message || 'Failed to reset password. Try again.';
      setErrors({ submit: msg });
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <>
        <LoginHeader />
        <section className="min-h-screen flex items-center justify-center p-4 relative">
          <div className="absolute inset-0 -z-10">
            <img src={yogBg} alt="Yoga background" className="w-full h-full object-cover object-center" />
            <div className="absolute inset-0 bg-gradient-to-r from-teal-900/80 to-emerald-900/80"></div>
          </div>
          <div className="relative z-10 text-center">
            <FaSpinner className="text-6xl text-white animate-spin mx-auto" />
            <p className="text-white mt-4 text-xl">Verifying OTP...</p>
          </div>
        </section>
        <Footer />
      </>
    );
  }

  if (!isValid) {
    return null;
  }

  if (isSuccess) {
    return (
      <>
        <LoginHeader />
        <section className="min-h-screen flex items-center justify-center p-4 relative">
          <div className="absolute inset-0 -z-10">
            <img src={yogBg} alt="Yoga background" className="w-full h-full object-cover object-center" />
            <div className="absolute inset-0 bg-gradient-to-r from-teal-900/80 to-emerald-900/80"></div>
          </div>
          <div className="relative z-10 w-full max-w-md my-8">
            <div className="bg-white rounded-xl shadow-2xl overflow-hidden border border-white/20 p-8 text-center">
              <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <FaCheckCircle className="text-3xl text-green-600" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Password Reset Successful!</h2>
              <p className="text-gray-600 mb-6">
                Your password has been successfully reset. You can now login with your new password.
              </p>
              <p className="text-sm text-gray-500">Redirecting to login page...</p>
            </div>
          </div>
        </section>
        <Footer />
      </>
    );
  }

  return (
    <>
      <LoginHeader />
      <section className="min-h-screen flex items-center justify-center p-4 relative">
        <div className="absolute inset-0 -z-10">
          <img src={yogBg} alt="Yoga background" className="w-full h-full object-cover object-center" />
          <div className="absolute inset-0 bg-gradient-to-r from-teal-900/80 to-emerald-900/80"></div>
        </div>

        <div className="relative z-10 w-full max-w-md my-8">
          <div className="bg-white rounded-xl shadow-2xl overflow-hidden border border-white/20">
            <div className="bg-gradient-to-r from-teal-600 to-emerald-600 p-6 text-center">
              <h2 className="text-2xl md:text-3xl font-bold text-white">Set New Password</h2>
              <p className="text-white/80 mt-1">Enter your new password below</p>
            </div>

            <Formik
              initialValues={{ password: '', confirmPassword: '' }}
              validationSchema={validationSchema}
              onSubmit={handleSubmit}
            >
              {({ isSubmitting }) => (
                <Form className="p-6 space-y-6">
                  <ErrorMessage name="submit" component="div" className="text-red-600 text-sm text-center" />

                  <div className="space-y-2">
                    <label htmlFor="password" className="text-sm font-medium text-gray-700">
                      New Password
                      <span className="text-red-500 ml-1" aria-label="required">*</span>
                    </label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" aria-hidden="true">
                        <FaLock className="h-5 w-5 text-gray-400" />
                      </div>
                      <Field
                        name="password"
                        id="password"
                        type={showPassword ? "text" : "password"}
                        placeholder="Enter new password"
                        className="pl-10 pr-10 w-full border rounded-lg py-2 px-3 focus:ring-teal-500"
                        aria-required="true"
                        aria-describedby="password-error"
                        autoComplete="new-password"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute inset-y-0 right-0 pr-3 flex items-center"
                        aria-label={showPassword ? "Hide password" : "Show password"}
                        tabIndex={-1}
                      >
                        {showPassword ? (
                          <FaEyeSlash className="h-5 w-5 text-gray-400" />
                        ) : (
                          <FaEye className="h-5 w-5 text-gray-400" />
                        )}
                      </button>
                    </div>
                    <ErrorMessage 
                      name="password" 
                      component="p" 
                      id="password-error"
                      className="text-red-500 text-xs" 
                      role="alert"
                      aria-live="polite"
                    />
                  </div>

                  <div className="space-y-2">
                    <label htmlFor="confirmPassword" className="text-sm font-medium text-gray-700">
                      Confirm Password
                      <span className="text-red-500 ml-1" aria-label="required">*</span>
                    </label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" aria-hidden="true">
                        <FaLock className="h-5 w-5 text-gray-400" />
                      </div>
                      <Field
                        name="confirmPassword"
                        id="confirmPassword"
                        type={showConfirmPassword ? "text" : "password"}
                        placeholder="Confirm new password"
                        className="pl-10 pr-10 w-full border rounded-lg py-2 px-3 focus:ring-teal-500"
                        aria-required="true"
                        aria-describedby="confirmPassword-error"
                        autoComplete="new-password"
                      />
                      <button
                        type="button"
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        className="absolute inset-y-0 right-0 pr-3 flex items-center"
                        aria-label={showConfirmPassword ? "Hide password" : "Show password"}
                        tabIndex={-1}
                      >
                        {showConfirmPassword ? (
                          <FaEyeSlash className="h-5 w-5 text-gray-400" />
                        ) : (
                          <FaEye className="h-5 w-5 text-gray-400" />
                        )}
                      </button>
                    </div>
                    <ErrorMessage 
                      name="confirmPassword" 
                      component="p" 
                      id="confirmPassword-error"
                      className="text-red-500 text-xs" 
                      role="alert"
                      aria-live="polite"
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className={`w-full bg-gradient-to-r from-teal-600 to-emerald-600 text-white py-3 px-4 rounded-lg flex items-center justify-center gap-2 transition-all shadow-md hover:shadow-lg ${
                      isSubmitting ? 'opacity-70 cursor-not-allowed' : 'hover:from-teal-700 hover:to-emerald-700'
                    }`}
                  >
                    {isSubmitting ? (
                      <>
                        <FaSpinner className="animate-spin" />
                        <span>Resetting...</span>
                      </>
                    ) : (
                      <span>Reset Password</span>
                    )}
                  </button>

                  <div className="text-center pt-4 border-t border-gray-200">
                    <Link to="/login" className="text-sm text-teal-600 hover:text-teal-500">
                      Back to Login
                    </Link>
                  </div>
                </Form>
              )}
            </Formik>
          </div>
        </div>
      </section>
      <Footer />
    </>
  );
};

export default ResetPassword;
